import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { toast } from "sonner";
import { Calendar, Clock3, CheckCheck, XCircle, ListChecks, Euro, MessageSquare, MessageCircle, Image as ImageIcon, TrendingUp, Sparkles, MousePointerClick, FlaskConical, Check, Play, Trash2, RotateCcw, Building2 } from "lucide-react";
import AdminChatPanel from "../components/AdminChatPanel";
import AdminGalleryUploader from "../components/AdminGalleryUploader";
import AdminTeaserStats from "../components/AdminTeaserStats";
import AdminBlogGenerator from "../components/AdminBlogGenerator";
import AdminSubClicksStats from "../components/AdminSubClicksStats";
import AdminABTests from "../components/AdminABTests";
import AdminBookingAnalytics from "../components/AdminBookingAnalytics";
import AdminB2BLeads from "../components/AdminB2BLeads";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("tab");
    if (p === "chat") return "chat";
    if (p === "gallery") return "gallery";
    if (p === "teasers") return "teasers";
    if (p === "blog") return "blog";
    if (p === "subclicks") return "subclicks";
    if (p === "abtests") return "abtests";
    if (p === "analytics") return "analytics";
    if (p === "leads") return "leads";
    return "bookings";
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login", { replace: true });
      else if (!user.is_admin) navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/admin/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } finally { setFetching(false); }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(t.admin.email_sent || "Updated");
      load();
    } catch { toast.error("Error"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm(t.admin.delete_confirm)) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("OK");
      load();
    } catch { toast.error("Error"); }
  };

  const deleteTestBookings = async () => {
    if (!window.confirm(t.admin.delete_test_confirm)) return;
    try {
      const res = await api.delete(`/bookings/test-data`);
      toast.success(`${res.data.deleted} ✕`);
      load();
    } catch { toast.error("Error"); }
  };

  if (loading || !user || !user.is_admin) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">{t.common.loading}</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
      <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-2">Admin</p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-10" data-testid="admin-title">{t.admin.title}</h1>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-8 border-b border-neutral-200">
        <button
          type="button"
          onClick={() => setTab("bookings")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "bookings" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-bookings"
        >
          <ListChecks size={14} className="inline me-1.5 -mt-0.5" />
          {t.admin.title}
        </button>
        <button
          type="button"
          onClick={() => setTab("chat")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "chat" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-chat"
        >
          <MessageCircle size={14} className="inline me-1.5 -mt-0.5" />
          {t.chat.admin_title}
        </button>
        <button
          type="button"
          onClick={() => setTab("gallery")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "gallery" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-gallery"
        >
          <ImageIcon size={14} className="inline me-1.5 -mt-0.5" />
          Galerie
        </button>
        <button
          type="button"
          onClick={() => setTab("teasers")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "teasers" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-teasers"
        >
          <TrendingUp size={14} className="inline me-1.5 -mt-0.5" />
          Teasers
        </button>
        <button
          type="button"
          onClick={() => setTab("blog")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "blog" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-blog"
        >
          <Sparkles size={14} className="inline me-1.5 -mt-0.5" />
          KI-Blog
        </button>
        <button
          type="button"
          onClick={() => setTab("subclicks")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "subclicks" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-subclicks"
        >
          <MousePointerClick size={14} className="inline me-1.5 -mt-0.5" />
          Sub-Klicks
        </button>
        <button
          type="button"
          onClick={() => setTab("abtests")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "abtests" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-abtests"
        >
          <FlaskConical size={14} className="inline me-1.5 -mt-0.5" />
          A/B Tests
        </button>
        <button
          type="button"
          onClick={() => setTab("leads")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "leads" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-leads"
        >
          <Building2 size={14} className="inline me-1.5 -mt-0.5" />
          B2B Leads
        </button>
        <button
          type="button"
          onClick={() => setTab("analytics")}
          className={`px-5 py-3 text-sm font-bold tracking-wide uppercase transition border-b-2 ${
            tab === "analytics" ? "border-[#FF5A00] text-[#FF5A00]" : "border-transparent text-neutral-500 hover:text-neutral-900"
          }`}
          data-testid="admin-tab-analytics"
        >
          <TrendingUp size={14} className="inline me-1.5 -mt-0.5" />
          Analytics
        </button>
      </div>

      {tab === "chat" ? (
        <AdminChatPanel />
      ) : tab === "leads" ? (
        <AdminB2BLeads />
      ) : tab === "analytics" ? (
        <AdminBookingAnalytics />
      ) : tab === "gallery" ? (
        <AdminGalleryUploader />
      ) : tab === "teasers" ? (
        <AdminTeaserStats />
      ) : tab === "blog" ? (
        <AdminBlogGenerator />
      ) : tab === "subclicks" ? (
        <AdminSubClicksStats />
      ) : tab === "abtests" ? (
        <AdminABTests />
      ) : (
      <>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          <StatCard icon={ListChecks} label="Buchungen total" value={stats.bookings.total} accent />
          <StatCard icon={Calendar} label="Diese Woche" value={stats.bookings.last_7_days} />
          <StatCard icon={Clock3} label="Ausstehend" value={stats.bookings.pending} />
          <StatCard icon={CheckCheck} label="Abgeschlossen" value={stats.bookings.completed} />
          <StatCard icon={Euro} label="Anzahlungen €" value={`${stats.revenue_eur.toFixed(2)} €`} />
          <StatCard icon={MessageSquare} label="Nachrichten" value={stats.contact_messages} />
        </div>
      )}

      {fetching ? <div className="text-neutral-500">{t.common.loading}</div> : (
        <>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">{bookings.length} {t.admin.title}</div>
          <button
            type="button"
            onClick={deleteTestBookings}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-600 hover:text-white hover:bg-red-600 border border-red-300 px-3 py-1.5 transition-colors"
            data-testid="admin-btn-delete-test-bookings"
          >
            <Trash2 size={13} /> {t.admin.delete_test}
          </button>
        </div>
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-start px-4 py-3">{t.admin.customer}</th>
                <th className="text-start px-4 py-3">{t.admin.service}</th>
                <th className="text-start px-4 py-3">{t.admin.datetime}</th>
                <th className="text-start px-4 py-3">{t.booking.address}</th>
                <th className="text-start px-4 py-3">{t.admin.status}</th>
                <th className="text-start px-4 py-3">{t.admin.actions}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b.booking_id} className="border-b border-neutral-100" data-testid={`admin-row-${b.booking_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold">{b.user_name}</div>
                    <div className="text-xs text-neutral-500">{b.user_email}</div>
                    <div className="text-xs text-neutral-500">{b.phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    {t.services[b.service]?.name || t.specialty[b.service]?.name || b.service}
                    {b.deposit_paid && <div className="inline-block ms-2 text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">PAID</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{b.date}<br/>{b.time}</td>
                  <td className="px-4 py-3 max-w-xs">{b.address}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold uppercase tracking-widest">{t.dashboard.status[b.status]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {b.status !== "confirmed" && b.status !== "completed" && b.status !== "cancelled" && (
                        <button
                          type="button"
                          onClick={() => updateStatus(b.booking_id, "confirmed")}
                          className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-2.5 py-1.5 uppercase tracking-wider transition-colors"
                          data-testid={`admin-btn-confirm-${b.booking_id}`}
                          title={t.admin.confirm}
                        >
                          <Check size={13} /> {t.admin.confirm}
                        </button>
                      )}
                      {b.status === "confirmed" && (
                        <button
                          type="button"
                          onClick={() => updateStatus(b.booking_id, "in_progress")}
                          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-2.5 py-1.5 uppercase tracking-wider transition-colors"
                          data-testid={`admin-btn-start-${b.booking_id}`}
                        >
                          <Play size={13} /> {t.dashboard.status.in_progress}
                        </button>
                      )}
                      {(b.status === "confirmed" || b.status === "in_progress") && (
                        <button
                          type="button"
                          onClick={() => updateStatus(b.booking_id, "completed")}
                          className="inline-flex items-center gap-1 bg-neutral-900 hover:bg-black text-white text-xs font-bold px-2.5 py-1.5 uppercase tracking-wider transition-colors"
                          data-testid={`admin-btn-complete-${b.booking_id}`}
                        >
                          <CheckCheck size={13} /> {t.admin.complete}
                        </button>
                      )}
                      {b.status !== "cancelled" && b.status !== "completed" && (
                        <button
                          type="button"
                          onClick={() => updateStatus(b.booking_id, "cancelled")}
                          className="inline-flex items-center gap-1 bg-white hover:bg-red-50 text-red-600 border border-red-300 text-xs font-bold px-2.5 py-1.5 uppercase tracking-wider transition-colors"
                          data-testid={`admin-btn-cancel-${b.booking_id}`}
                        >
                          <XCircle size={13} /> {t.admin.cancel}
                        </button>
                      )}
                      {(b.status === "cancelled" || b.status === "completed") && (
                        <button
                          type="button"
                          onClick={() => updateStatus(b.booking_id, "pending")}
                          className="inline-flex items-center gap-1 bg-white hover:bg-neutral-100 text-neutral-700 border border-neutral-300 text-xs font-bold px-2.5 py-1.5 uppercase tracking-wider transition-colors"
                          data-testid={`admin-btn-reopen-${b.booking_id}`}
                        >
                          <RotateCcw size={13} /> {t.admin.reopen}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => deleteBooking(b.booking_id)}
                        className="inline-flex items-center gap-1 text-neutral-400 hover:text-red-600 text-xs font-bold px-1.5 py-1.5 transition-colors"
                        data-testid={`admin-btn-delete-${b.booking_id}`}
                        title={t.admin.delete}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={6} className="text-center text-neutral-400 py-10">—</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "bg-[#090A0B] text-white border-[#090A0B]" : "bg-white border-neutral-200"}`} data-testid={`stat-${label}`}>
      <Icon size={18} className={accent ? "text-[#FF5A00]" : "text-neutral-400"} />
      <div className="text-2xl font-black tracking-tight mt-3">{value}</div>
      <div className={`text-[10px] font-bold tracking-widest uppercase mt-1 ${accent ? "text-neutral-400" : "text-neutral-500"}`}>{label}</div>
    </div>
  );
}
